import { useId, type ReactNode } from "react";
import { defaultNumberFormat, estimateSuffix, type Measure } from "./estimate.ts";

/**
 * Sparkline: a compact inline trend (design 12.2 headline tiles, service rows).
 *
 * It is a tiny self-contained SVG rather than a full uPlot instance — a sparkline sits inline in a
 * table cell or a tile where a canvas lifecycle, resize observer and legend would be overkill, and
 * an SVG scales crisply at that size and prints cleanly. The path is drawn in `viewBox` units and
 * stretched to the box, so it needs no measurement pass.
 *
 * Accessibility (design 12.4): the drawn line is `aria-hidden`; the component ALWAYS renders a text
 * alternative — the latest value and the trend direction in words (e.g. "42 /s, up"), plus a
 * visually-hidden sentence for assistive tech. Confidence is surfaced too: a sampled latest value
 * carries "est." (design 12.3). The chart is therefore never the only way to read the trend.
 */

export interface SparklineProps {
  /** The series to draw, oldest → newest. Fewer than two points draws no line (shows text only). */
  readonly values: readonly number[];
  /** A unit shown after the latest value in the text alternative, e.g. "/s", "ms". */
  readonly unit?: string;
  /** Stroke colour: a `--token` resolves from the theme, else used verbatim. Defaults to accent. */
  readonly colorVar?: string;
  /** Width/height of the drawing box in CSS px. Default 80×20. */
  readonly width?: number;
  readonly height?: number;
  /** Confidence in the latest value (design 12.3 "est."). */
  readonly measure?: Measure;
  /** Format the latest value for the text alternative. Defaults to a grouped number. */
  readonly format?: (value: number) => string;
  /** An accessible label prefix, e.g. "Decisions". Combined with the value in the a11y text. */
  readonly label?: string;
}

type Trend = "up" | "down" | "flat";

/** Compare the last two points to describe the direction in words (never colour alone). */
function trendOf(values: readonly number[]): Trend {
  if (values.length < 2) return "flat";
  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  if (last === undefined || prev === undefined || last === prev) return "flat";
  return last > prev ? "up" : "down";
}

const TREND_GLYPH: Readonly<Record<Trend, string>> = { up: "▲", down: "▼", flat: "→" };

/** Map values into a `0..width`/`0..height` polyline, flipping y so larger values sit higher. */
function toPoints(values: readonly number[], width: number, height: number): string {
  if (values.length < 2) return "";
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return "";
  const span = max - min || 1;
  const stepX = width / (values.length - 1);
  const pad = 1; // keep the stroke off the exact edge
  const usable = height - pad * 2;
  return values
    .map((v, i) => {
      const safe = Number.isFinite(v) ? v : min;
      const y = pad + (usable - ((safe - min) / span) * usable);
      return `${(i * stepX).toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

export function Sparkline(props: SparklineProps): ReactNode {
  const width = props.width ?? 80;
  const height = props.height ?? 20;
  const titleId = useId();
  const format = props.format ?? defaultNumberFormat;
  const values = props.values;
  const latest = values.length > 0 ? values[values.length - 1] : undefined;
  const trend = trendOf(values);
  const stroke =
    props.colorVar === undefined
      ? "var(--accent)"
      : props.colorVar.startsWith("--")
        ? `var(${props.colorVar})`
        : props.colorVar;

  const latestText =
    latest === undefined || !Number.isFinite(latest)
      ? "no data"
      : `${format(latest)}${props.unit ?? ""}${estimateSuffix(props.measure)}`;
  const prefix = props.label === undefined ? "" : `${props.label}: `;
  const trendWord = trend === "up" ? "up" : trend === "down" ? "down" : "flat";
  const a11yText = `${prefix}${latestText}, trend ${trendWord}`;
  const points = toPoints(values, width, height);

  return (
    <span className="sparkline" data-trend={trend}>
      <svg
        className="sparkline__svg"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby={titleId}
        preserveAspectRatio="none"
      >
        <title id={titleId}>{a11yText}</title>
        {points.length > 0 && (
          <polyline
            points={points}
            fill="none"
            stroke={stroke}
            strokeWidth={1.25}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      {/* The visible text alternative: the latest value and a direction glyph + word, never a bare
          line. The glyph is decorative; the word carries the direction for non-colour readers. */}
      <span className="sparkline__text" aria-hidden="true">
        <span className="sparkline__value">{latestText}</span>
        <span className="sparkline__trend" data-trend={trend}>
          {TREND_GLYPH[trend]}
        </span>
      </span>
      <span className="sr-only">{a11yText}</span>
    </span>
  );
}
